import type { Monaco } from '@monaco-editor/react';
import type { editor, MarkerSeverity } from 'monaco-editor';
import * as prettier from 'prettier';
import * as prettierPluginGraphql from 'prettier/plugins/graphql';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { WidgetProps } from '../../../../schema-form/types.js';
import { getWidgetId } from '../../../../schema-form/util/widget.js';
import { useDebouncedChangeHandler } from '../../../hooks/use-debounced-change-handler.js';
import useInit from '../../../hooks/use-init.js';
import useWatch from '../../../hooks/use-watch.js';

export type GraphQLOwnProps = WidgetProps;

const formatGraphQL = async (value: string) => {
  return prettier.format(value, {
    parser: 'graphql',
    plugins: [prettierPluginGraphql]
  });
};

export const useGraphQL = (props: GraphQLOwnProps) => {
  const { value, onChange, config } = props;
  const monacoInstanceRef = useRef<Monaco>();
  const editorInstanceRef = useRef<editor.IStandaloneCodeEditor>();
  const editInProgressRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [initialValue, setInitialValue] = useState<string | null>(null);
  const [initialError, setInitialError] = useState<Error | null>(null);
  const hideByDefault = config.get('hideByDefault');
  const [visible, setVisible] = useState(Boolean(!hideByDefault || value));

  useInit({
    onInit() {
      void (async () => {
        let result = value as string;
        try {
          result = await formatGraphQL(value as string);
        } catch (error) {
          // Need to wait until Monaco is ready before we handle the error
          // and can't initialize Monaco without an initial value.
          setInitialError(error);
        }

        setInitialValue(result);
      })();
    }
  });

  // If the path changes, we may not have reinitialized, but we need to update the value
  useWatch({
    value,
    onChange(value) {
      if (editInProgressRef.current) {
        editInProgressRef.current = false;
      } else if (editorInstanceRef.current) {
        editInProgressRef.current = true;
        editorInstanceRef.current.setValue(value as string);
      }
    }
  });

  const handleValidationError = useCallback((error: Error | undefined) => {
    if (editorInstanceRef.current && monacoInstanceRef.current) {
      const model = editorInstanceRef.current.getModel();
      if (model) {
        const markers: editor.IMarkerData[] = [];
        if (error) {
          const where = /\((\d+):(\d+)\)/.exec(error.message);
          const startLineNumber = where ? Number(where[1]) : 1;
          const endLineNumber = where ? startLineNumber : 1;
          const startColumn = where ? Number(where[2]) : 1;
          const endColumn = where ? startColumn : 1;
          if (model) {
            markers.push({
              message: error.message,
              severity: 8 as MarkerSeverity,
              startLineNumber,
              endLineNumber,
              startColumn,
              endColumn
            });
          }
        }

        monacoInstanceRef.current.editor.setModelMarkers(model, 'TakeShape', markers);
        setError(
          markers.length === 0
            ? null
            : markers.length === 1
              ? markers[0].message.split(/\n+/)[0]
              : `${markers.length} errors in selection set`
        );
      }
    }
  }, []);

  useInit({
    value: initialError && editorInstanceRef.current && monacoInstanceRef.current ? initialError : undefined,
    onValueInit: useCallback(
      (error: Error) => {
        handleValidationError(error);
      },
      [handleValidationError]
    )
  });

  const validate = useCallback(async () => {
    if (editorInstanceRef.current && monacoInstanceRef.current) {
      let validationError: Error | undefined;
      try {
        await formatGraphQL(editorInstanceRef.current.getValue());
      } catch (error) {
        validationError = error;
      }

      handleValidationError(validationError);
    }
  }, [handleValidationError]);

  const debouncedValidate = useDebouncedChangeHandler(validate, 500);

  const onChangeAndMinify = useCallback(
    (value: unknown) => {
      if (editInProgressRef.current) {
        editInProgressRef.current = false;
      } else {
        editInProgressRef.current = true;
        onChange(value);
        debouncedValidate.run();
      }
    },
    [debouncedValidate, onChange]
  );

  const prettify = useCallback(async () => {
    if (editorInstanceRef.current && monacoInstanceRef.current) {
      try {
        editorInstanceRef.current.setValue(await formatGraphQL(editorInstanceRef.current.getValue()));
      } catch {
        // Ignore error here because validation will show it
      }
    }
  }, []);

  const handleShow = useCallback(() => {
    setVisible(true);
  }, []);

  const label = props.config.get('label') || 'Edit GraphQL';

  const id = useMemo(() => getWidgetId(props), [props]);

  return {
    ...props,
    visible,
    initialValue,
    onChange: onChangeAndMinify,
    prettify,
    monacoInstanceRef,
    editorInstanceRef,
    error,
    handleShow,
    id,
    label
  };
};

export type GraphQLProps = ReturnType<typeof useGraphQL>;
